import { useState } from "react";
import styled from "styled-components";

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  createFilterOptions,
  Autocomplete,
} from "@mui/material";
import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import { IconButton, Stack, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";

import TextField from "src/components/core/TextField";

import { Black, White, White01 } from "src/styles/color";

import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";

import { updateSellerListHome } from "src/api/seller";
import { hexToRgba } from "src/common/color";

const S = {};

S.BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: ${White};
  box-shadow: 0px 4px 20px ${hexToRgba(Black, 0.1)};
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

S.BannerListBoxWrapper = styled.div`
  position: relative;
  padding: 24px;
  background-color: ${White01};
  border-radius: 16px;
  margin-top: 24px;
  width: 100%;
  overflow: hidden;
  min-height: 200px;
`;

S.BannerListContainer = styled.div`
  width: fit-content;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px;
  margin: 0 auto;
`;

S.SellerLogoItem = styled.img`
  width: 160px;
  height: 80px;
  border-radius: 16px;
  background: ${White};
  object-fit: contain;
  cursor: pointer;
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const ImgDisplay = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  flex-shrink: 0;
`;

const MAX_POPULAR_SELLER = 999;

const PopularSellerEditDialog = ({ initialValue, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useDialog();

  const [sellers] = useState(initialValue?.sellers || []);
  const [selectedSellers, setSelectedSellers] = useState(
    initialValue?.sellers?.filter((seller) => seller?.is_show_homepage) || []
  );

  const handleSelectSeller = (sellers) => {
    if (sellers.length > MAX_POPULAR_SELLER) {
      enqueueSnackbar(
        `สามารถเลือกร้านค้าได้ไม่เกิน ${MAX_POPULAR_SELLER} ร้าน`,
        {
          variant: "error",
        }
      );
      return;
    }
    setSelectedSellers(sellers);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        sellerIds: selectedSellers.map((seller) => seller?._id),
      };

      await updateSellerListHome(payload);

      enqueueSnackbar(`บันทึกสำเร็จ`, {
        variant: "success",
      });

      onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: 1440,
            maxWidth: "100%",
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3, textAlign: "center" }}>
        แก้ไขร้านค้ายอดนิยม
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Autocomplete
          // freeSolo
          multiple
          aria-required={true}
          // getOptionSelected={(option, value) =>
          //   option?._id === value?._id
          // }
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          renderOption={(props, option, { selected }) => (
            <li
              {...props}
              key={`item-${option?._id}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {!!option?.profile_image === false ? (
                  <ImageEmpty>
                    <InsertPhotoIcon
                      sx={{
                        color: "rgba(0, 0, 0, 0.54)",
                        cursor: "pointer",
                      }}
                    />
                  </ImageEmpty>
                ) : (
                  <ImgDisplay
                    src={option?.profile_image ? option?.profile_image : ""}
                    alt={option?.profile_image}
                  />
                )}
                <Stack style={{ marginLeft: 16 }}>
                  <Typography component="span">{option?.brand_name}</Typography>
                </Stack>
              </Box>
            </li>
          )}
          options={sellers}
          value={selectedSellers}
          onChange={(e, newValue) => {
            handleSelectSeller(newValue);
          }}
          getOptionLabel={(option) => {
            return option?.brand_name || "--";
          }}
          filterOptions={createFilterOptions({
            matchFrom: "any",
            limit: 100,
          })}
          sx={{
            width: "100%",
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                maxWidth: "100%",
                width: "100%",
              }}
              required
              placeholder="ค้นหาร้านค้า"
              helperText={`เลือกได้ไม่เกิน ${MAX_POPULAR_SELLER} ร้าน`}
              // error={!selectedSeller?._id ? true : false}
            />
          )}
        />
        <S.BannerListBoxWrapper>
          <S.BannerListContainer>
            {selectedSellers.map((seller) => (
              <S.SellerLogoItem
                key={seller?._id}
                src={seller?.icon_homepage || seller?.profile_image}
                alt={seller?.brand_name}
              />
            ))}
          </S.BannerListContainer>
        </S.BannerListBoxWrapper>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            minWidth: 160,
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            openDialog({
              type: "confirm",
              title: initialValue
                ? `คุณต้องการยืนยันการแก้ไขใช่หรือไม่`
                : `คุณต้องการเพิ่มร้านค้าใช่หรือไม่?`,
              onConfirm: async () => {
                await handleSubmit();
              },
              maxWidth: 342,
            });
          }}
          sx={{
            minWidth: 160,
          }}
          // autoFocus // enable auto focus to increase accessibility
          // disabled={!isFormValid || !isMetaTagValid}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PopularSellerEditDialog;
